import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import type { ClientChartBundle } from '../pages/clients/data/clientChartBundle';
import { fetchMedplumClientChartBundle } from '../lib/medplumChartBundle';

/**
 * Loads Medical Record / chart bundle data from Medplum for one patient.
 */
export function useMedplumClientChartBundle(
  patientId: string | undefined,
): {
  bundle: ClientChartBundle | undefined;
  loading: boolean;
  error: Error | null;
} {
  const medplum = useMedplum();
  const [bundle, setBundle] = useState<ClientChartBundle | undefined>();
  const [loading, setLoading] = useState(!!patientId);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!patientId) {
      setBundle(undefined);
      setError(null);
      return;
    }
    const resolvedPatientId = patientId;
    let cancelled = false;
    async function run() {
      setLoading(true);
      try {
        const b = await fetchMedplumClientChartBundle(medplum, resolvedPatientId);
        if (!cancelled) {
          setBundle(b);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [medplum, patientId]);

  return { bundle, loading, error };
}
